import React from 'react'
import { AdmIcn } from './AdminIcons'

// ─── Page header ─────────────────────────────────────────────────────────────

interface AdminPageHeaderProps {
  eyebrow?: string
  title: string
  titleItalic?: string
  sub?: string
  breadcrumb?: string[]
  actions?: React.ReactNode
}

export function AdminPageHeader({ eyebrow, title, titleItalic, sub, breadcrumb, actions }: AdminPageHeaderProps) {
  return (
    <div className="mb-6">
      {breadcrumb && (
        <div className="mb-2 text-[12px] text-muted flex items-center gap-1.5">
          {breadcrumb.map((b, i) => (
            <React.Fragment key={i}>
              {i > 0 && <AdmIcn name="chev" size={11} />}
              <span className={i === breadcrumb.length - 1 ? 'text-ink-2' : 'text-muted'}>{b}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      <div className="flex items-end justify-between gap-6">
        <div className="min-w-0">
          {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
          <h1 className="serif m-0 text-[42px] leading-[1.02] tracking-[-0.025em] font-medium">
            {title}
            {titleItalic && <> <span className="serif-i text-primary">{titleItalic}</span></>}
          </h1>
          {sub && <div className="mt-2 text-[14px] text-ink-2 max-w-[640px]">{sub}</div>}
        </div>
        {actions && <div className="flex gap-2.5 shrink-0">{actions}</div>}
      </div>
    </div>
  )
}

// ─── Card ────────────────────────────────────────────────────────────────────

interface AdminCardProps {
  children: React.ReactNode
  padding?: number | string
  title?: string
  action?: React.ReactNode
  style?: React.CSSProperties
}

export function AdminCard({ children, padding = 18, title, action, style }: AdminCardProps) {
  return (
    <section
      className="bg-surface border border-line-soft rounded-[18px] overflow-hidden"
      style={style}
    >
      {title && (
        <div className="flex items-center justify-between px-[18px] pt-4 pb-2.5">
          <div className="serif text-[19px] tracking-[-0.015em]">{title}</div>
          {action}
        </div>
      )}
      <div style={{ padding: title ? `0 ${typeof padding === 'number' ? padding : 18}px ${typeof padding === 'number' ? padding : 18}px` : padding }}>
        {children}
      </div>
    </section>
  )
}

// ─── Button ──────────────────────────────────────────────────────────────────

type AdmBtnVariant = 'primary' | 'ghost' | 'soft' | 'ochre' | 'danger'
type AdmBtnSize = 'sm' | 'md' | 'lg'

interface AdmBtnProps {
  children?: React.ReactNode
  icon?: string
  variant?: AdmBtnVariant
  size?: AdmBtnSize
  block?: boolean
  className?: string
  onClick?: () => void
  disabled?: boolean
  style?: React.CSSProperties
  type?: 'button' | 'submit' | 'reset'
}

const admbtnSize: Record<AdmBtnSize, string> = {
  sm: 'h-[30px] px-3 text-[12px]',
  md: 'h-[42px] px-4 text-[13px]',
  lg: 'h-[52px] px-[22px] text-[14.5px]',
}

const admbtnVariant: Record<AdmBtnVariant, string> = {
  primary: 'bg-primary text-on-primary border-0 font-semibold',
  ghost:   'bg-transparent text-ink border border-line font-medium',
  soft:    'bg-s2 text-ink border border-line-soft font-medium',
  ochre:   'bg-ochre text-white border-0 font-semibold',
  danger:  'bg-transparent text-[#A33A2A] border border-[#c89086] font-medium',
}

const admbtnIconSize: Record<AdmBtnSize, number> = { sm: 14, md: 15, lg: 17 }

export function AdmBtn({ children, icon, variant = 'ghost', size = 'md', block, className, onClick, disabled, style, type = 'button' }: AdmBtnProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-full inline-flex items-center gap-1.5 whitespace-nowrap font-sans disabled:opacity-50 disabled:cursor-not-allowed ${admbtnSize[size]} ${admbtnVariant[variant]}${block ? ' w-full justify-start' : ''}${className ? ` ${className}` : ''}`}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer', ...style }}
    >
      {icon && <AdmIcn name={icon} size={admbtnIconSize[size]} />}
      {children}
    </button>
  )
}

// ─── Form atoms ──────────────────────────────────────────────────────────────

interface FormFieldProps {
  label: string
  hint?: string
  required?: boolean
  error?: string
  children: React.ReactNode
  style?: React.CSSProperties
  htmlFor?: string
}

export function FormField({ label, hint, required, error, children, style, htmlFor }: FormFieldProps) {
  return (
    <div className="flex flex-col gap-1.5" style={style}>
      <label htmlFor={htmlFor} className="text-[12.5px] font-semibold text-ink-2">
        {label}{required && <span className="text-warn"> *</span>}
      </label>
      {children}
      {hint && <div className="text-[11.5px] text-muted leading-[1.4]">{hint}</div>}
      {error && <div className="text-[11.5px] text-danger">{error}</div>}
    </div>
  )
}

interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: string
  suffix?: string
  inputSize?: 'sm' | 'md' | 'lg'
}

const textInputH = { lg: 'h-[52px]', md: 'h-[42px]', sm: 'h-[34px]' }
const textInputFs = { lg: 'text-[15px]', md: 'text-[14px]', sm: 'text-[14px]' }

export function TextInput({ icon, suffix, inputSize = 'md', style, className, ...rest }: TextInputProps) {
  return (
    <div
      className={`flex items-center gap-2 px-3.5 rounded-[10px] bg-s2 border border-line ${textInputH[inputSize]}`}
      style={style}
    >
      {icon && <span className="text-muted shrink-0"><AdmIcn name={icon} size={15} /></span>}
      <input
        className={`flex-1 border-0 bg-transparent outline-none text-ink font-sans min-w-0 ${textInputFs[inputSize]} ${className ?? ''}`}
        {...rest}
      />
      {suffix && <span className="text-[11.5px] text-muted mono shrink-0">{suffix}</span>}
    </div>
  )
}

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  rows?: number
}

export function AdminTextArea({ rows = 4, style, className, ...rest }: TextAreaProps) {
  return (
    <textarea
      rows={rows}
      className={`px-3.5 py-3 rounded-[10px] resize-y bg-s2 border border-line text-[14px] text-ink font-sans leading-relaxed outline-none w-full box-border ${className ?? ''}`}
      style={style}
      {...rest}
    />
  )
}

interface ToggleProps {
  on: boolean
  onChange?: (val: boolean) => void
  label?: string
  sub?: string
}

export function Toggle({ on, onChange, label, sub }: ToggleProps) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <button
        type="button"
        onClick={() => onChange?.(!on)}
        className={`w-10 h-6 rounded-full shrink-0 border-0 cursor-pointer relative p-0 transition-[background] duration-150 ${on ? 'bg-primary' : 'bg-line'}`}
      >
        <div
          className="absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white transition-[left] duration-150"
          style={{ left: on ? 19 : 3, boxShadow: '0 1px 3px rgba(0,0,0,0.18)' }}
        />
      </button>
      {(label || sub) && (
        <div className="min-w-0">
          {label && <div className="text-[13.5px] font-medium">{label}</div>}
          {sub && <div className="text-[11.5px] text-muted mt-0.5">{sub}</div>}
        </div>
      )}
    </label>
  )
}

// ─── Confidence bars ─────────────────────────────────────────────────────────

interface ConfBarsProps {
  score: number
  max?: number
}

export function ConfBars({ score, max = 5 }: ConfBarsProps) {
  const color = score >= max - 1 ? '#22c55e' : score >= max - 2 ? '#f59e0b' : '#ef4444'
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex gap-0.5">
        {Array.from({ length: max }, (_, i) => (
          <div
            key={i}
            style={{ width: 5, height: 14, borderRadius: 1.5, background: i < score ? color : 'var(--line-soft)' }}
          />
        ))}
      </div>
      <span className="text-[11px] text-muted font-mono">{score}/{max}</span>
    </div>
  )
}

interface SegmentedOption { id: string; label: string }

interface SegmentedProps {
  value: string
  options: (string | SegmentedOption)[]
  onChange?: (val: string) => void
}

export function Segmented({ value, options, onChange }: SegmentedProps) {
  return (
    <div className="inline-flex p-[3px] rounded-full bg-s2 border border-line-soft">
      {options.map(o => {
        const id = typeof o === 'string' ? o : o.id
        const lbl = typeof o === 'string' ? o : o.label
        const active = value === id
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange?.(id)}
            className={`h-[30px] px-3.5 rounded-full border-0 text-[12.5px] cursor-pointer font-sans ${active ? 'bg-surface text-ink font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.05)]' : 'bg-transparent text-muted font-medium'}`}
          >
            {lbl}
          </button>
        )
      })}
    </div>
  )
}
